/**
 * Phase 20B: helpers for clinical templates (constants/clinicalTemplates.ts, generated from the
 * backend's clinical/templates.js). The backend applies the same required-field rules on submit.
 */
import { CLINICAL_TEMPLATES, ClinicalTemplate, TemplateField, TemplateSection } from '../../../constants/clinicalTemplates';
import { getDisciplineEntry } from '../../../constants/disciplines';

export type TemplateKind = 'assessment' | 'carePlan';
export type AssessmentTypeKey = 'initial' | 'recertification' | 'discharge';

export const ASSESSMENT_TYPE_LABELS: Record<AssessmentTypeKey, string> = {
  initial: 'Initial / Admission (SOC)',
  recertification: 'Recertification',
  discharge: 'Discharge',
};

export function getTemplate(id: string | null | undefined): ClinicalTemplate | null {
  return (CLINICAL_TEMPLATES as readonly ClinicalTemplate[]).find(t => t.id === id) || null;
}

/** The template a discipline uses for this record kind, or null (hand-built form or none). */
export function templateFor(discipline: string | null | undefined, kind: TemplateKind): ClinicalTemplate | null {
  const id = getDisciplineEntry(discipline)?.templates?.[kind];
  return id ? getTemplate(id) : null;
}

/** Disciplines whose record of this kind is template-driven (active disciplines only). */
export function templateDisciplines(kind: TemplateKind): string[] {
  return (CLINICAL_TEMPLATES as readonly ClinicalTemplate[])
    .filter(t => t.kind === kind && getDisciplineEntry(t.discipline, { includePlanned: false }))
    .map(t => t.discipline);
}

/**
 * Section condition, the backend grammar's subset used by templates:
 * `assessmentType === 'x'`, `assessmentType !== 'x'`, joined with && or ||. Unknown → shown.
 */
export function sectionShown(section: TemplateSection, assessmentType: string): boolean {
  if (!section.when) return true;
  try {
    return section.when.split('||').some(orPart => orPart.split('&&').every(term => {
      const m = /^\s*assessmentType\s*(===|!==)\s*'([^']*)'\s*$/.exec(term);
      if (!m) throw new Error('unsupported');
      return m[1] === '===' ? assessmentType === m[2] : assessmentType !== m[2];
    }));
  } catch {
    return true;
  }
}

export function fieldShown(field: TemplateField, assessmentType?: string): boolean {
  return !field.showFor || !assessmentType || field.showFor.includes(assessmentType);
}

/** Storage key: care plans use field.key; assessments "<Section>-<Field>" in data.categories. */
export const fieldKey = (template: ClinicalTemplate, section: TemplateSection, field: TemplateField): string =>
  template.kind === 'carePlan' ? (field.key as string) : `${section.name}-${field.name}`;

const blank = (v: unknown) => v === undefined || v === null || v === false || v === 'false'
  || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);

/** Required fields not filled in, as { key: message } (same rule the server applies). */
export function templateRequiredErrors(template: ClinicalTemplate, values: Record<string, unknown>, assessmentType?: string): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const section of template.sections) {
    if (template.kind === 'assessment' && !sectionShown(section, assessmentType || 'initial')) continue;
    for (const field of section.fields) {
      if (!field.required || !fieldShown(field, template.kind === 'assessment' ? assessmentType : undefined)) continue;
      const key = fieldKey(template, section, field);
      if (blank(values[key])) errors[key] = `${field.name} is required`;
    }
  }
  return errors;
}
